import eventBus from '@sd-utility/eventbus';
import { BASKET_ERROR_CODES } from 'constants/basket';
import { BasketAmendItemParams } from 'types/BasketTypes';
import { ODMBasketErrorResponse, ODMBasketResponse } from 'types/ODMTypes';

export const isODMBasketErrorResponse = (response: ODMBasketResponse): response is ODMBasketErrorResponse => {
  const errors = response?.error?.errors;
  return Array.isArray(errors) && errors.some((error) => BASKET_ERROR_CODES.includes(error.key));
};

export const getAmendBasketParams = (): BasketAmendItemParams => {
  const params = new URLSearchParams(window.location.search);

  return {
    amendItemId: params.get('amendItemId'),
    variantId: params.get('variantId'),
  };
};

export const isAmendBasketEnabled = (
  params: BasketAmendItemParams,
): params is { amendItemId: string; variantId: string } => !!(params.amendItemId && params.variantId);

/**
 * Waits for a basket service (ODM or Mock) to be ready.
 * Resolves when the service is initialized and, if applicable, the specified method is available.
 * Rejects if initialization fails or times out.
 *
 * @param serviceType The type of service to wait for (e.g., 'odm' | 'mock')
 * @param methodName Optional method name to check on the basket (e.g., 'addItem', 'amendItem')
 * @param timeoutMs Maximum time to wait in milliseconds (default 5000ms)
 */
export async function waitForODMReady(
  serviceType: 'odm' | 'mock',
  methodName?: keyof NonNullable<NonNullable<typeof window.ODM>['basket']>,
  timeoutMs = 5000,
): Promise<void> {
  const basket = serviceType === 'odm' ? window?.ODM?.basket : undefined;

  if (serviceType === 'odm' && basket && (!methodName || typeof basket[methodName] === 'function')) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let timedOut = false;

    if (!eventBus || typeof eventBus.subscribe !== 'function') {
      throw new Error('EventBus is not available to wait for ODM initialization.');
    }

    let unsubscribe: () => void = () => {};

    const timeout = setTimeout(() => {
      timedOut = true;
      unsubscribe();
      reject(new Error('ODM basket service did not initialize in time.'));
    }, timeoutMs);

    unsubscribe = eventBus.subscribe('odm/client-ready', (eventData: { initialized: boolean; error?: any }) => {
      if (timedOut) return;

      if (eventData?.initialized) {
        clearTimeout(timeout);
        unsubscribe();
        resolve();
      } else if (eventData && eventData.initialized === false) {
        clearTimeout(timeout);
        unsubscribe();
        reject(new Error(eventData.error || 'ODM basket service initialization failed.'));
      }
    }).unsubscribe;
  });
}
